/** @file Orders IK chains: user order, then after every chain whose solve moves a bone they read. */
import { throwError } from '@pose2d/format/throwError';
import { inBoneSubtree } from './inBoneSubtree';

/** One IK constraint in compiled indices (`ik` = its index in the rig's `ik` array). */
export interface IkChain {
  id: string;
  name: string;
  ik: number;
  bones: readonly number[];
  target: number;
}

/** True when `c` reads a bone `d` moves: c's target or the parent of c's first bone lies in d's subtree. */
function dependsOn(parents: readonly number[], c: IkChain, d: IkChain): boolean {
  const base = parents[c.bones[0] ?? -1] ?? -1;
  return inBoneSubtree(parents, c.target, d.bones) || (base >= 0 && inBoneSubtree(parents, base, d.bones));
}

/**
 * The dependency cycle that blocks the unplaced chains: from the first one (user order), follow an unplaced
 * dependency until a chain repeats; chains that merely wait on the cycle are not part of it. Every unplaced chain
 * has an unplaced dependency (else it would have been placed), so the walk always closes.
 */
function blockingCycle(chains: readonly IkChain[], deps: readonly IkChain[][], placed: ReadonlySet<IkChain>): IkChain[] {
  const walk: IkChain[] = [];
  let c = chains.find((x) => !placed.has(x));
  while (c !== undefined && !walk.includes(c)) {
    walk.push(c);
    c = (deps[chains.indexOf(c)] ?? []).find((d) => !placed.has(d));
  }
  return c === undefined ? walk : walk.slice(walk.indexOf(c));
}

/** CYCLE over the chains of one dependency cycle: params.path = their id chain (AR-9), the names in the hint. */
function throwCycle(cycle: readonly IkChain[]): never {
  const loop = [...cycle, ...cycle.slice(0, 1)];
  const names = loop.map((c) => c.name).join(' -> ');
  return throwError('CYCLE', { path: loop.map((c) => c.id).join(' -> ') }, { path: `constraints.${cycle[0]?.id ?? ''}`, hint: `each of these IK constraints reads a bone the next one moves: ${names}` });
}

/**
 * Stable topological order of `chains` (given in user order): a chain runs after every chain it depends on,
 * otherwise in the given order. CYCLE when a target lies in its own chain's subtree or chains depend on each other.
 */
export function sortIkChains(chains: readonly IkChain[], parents: readonly number[]): IkChain[] {
  for (const c of chains) {
    if (inBoneSubtree(parents, c.target, c.bones)) {
      throwError('CYCLE', { path: `${c.id} -> ${c.id}` }, { path: `constraints.${c.id}.target`, hint: `IK constraint "${c.name}": the target is inside the bones it moves` });
    }
  }
  const deps = chains.map((c) => chains.filter((d) => d !== c && dependsOn(parents, c, d)));
  const placed = new Set<IkChain>();
  const out: IkChain[] = [];
  while (out.length < chains.length) {
    const next = chains.find((c, i) => !placed.has(c) && (deps[i] ?? []).every((d) => placed.has(d)));
    if (next === undefined) throwCycle(blockingCycle(chains, deps, placed));
    placed.add(next);
    out.push(next);
  }
  return out;
}
